import { PAYMENT_GATEWAY } from "../../../core/constants/constants";
import { CheckoutPayload } from "../../../core/models/checkout-payload.model";
import { CreditCardModel } from "../../../core/models/credit-card.model";
import { GlobalConfig } from "../../../core/models/global-config.model";
import { PaymentGatewayModel } from "../../../core/models/payment-gateway.model";
import { CheckoutStripeWallet } from "./stripe-wallet.const";

const StripeCardNumberElementId = '#card-number';

export class CheckoutStripe {
  private stripeCardNumberElement?: stripe.elements.Element;
  private paymentGateway?: PaymentGatewayModel;
  private stripe?: stripe.Stripe;
    
  constructor(
    private readonly global: GlobalConfig,
    private readonly cardPaymentTypeAllowed: KnockoutObservable<boolean>,
    private readonly setErrorMessage: (error: string) => void,
    private readonly processPayment: (card: CreditCardModel) => void,
    private readonly onCardBrandChange: (cardType: string, cardNumberId: string, cardBrandId: string, surchargeId: string) => void,
    public readonly wallet: CheckoutStripeWallet
  ) { }

  public async initialise(paymentGateway: PaymentGatewayModel, walletGateway: PaymentGatewayModel) {
    if (!this.isValidGateway(paymentGateway) && !this.isValidGateway(walletGateway))
      return;

    await $.getScript('https://js.stripe.com/v3/')

    if (this.isValidGateway(paymentGateway)) {
      this.initialiseStripeCard(paymentGateway);
    }

    if (this.isValidGateway(walletGateway) && this.global.config.stripePaymentRequestEnabled) {
      this.wallet.initialise(walletGateway);
    }
  }

  public async purchase() {
    try {
      const result = await this.stripe.createSource(this.stripeCardNumberElement);
      if (result.error) {
        this.setErrorMessage(result.error.message);
        return;
      }
      // Send the token to your server.
      const creditCard: CreditCardModel = {
          cardBrand: result.source.card.brand,
          cardSource: result.source.id,
          cardCountry: result.source.card.country
      };

      if (!result.source.card.country) {
        creditCard.cardCountry = this.paymentGateway.domesticCardCountries[0];
      }
      
      this.processPayment(creditCard);
    } catch (error) {
      this.setErrorMessage(error.message);
    }
  }

  public confirmPurchase(paymentIntentSecret: string) {
    return this.stripe.confirmCardPayment(paymentIntentSecret, { payment_method: { card: this.stripeCardNumberElement } })
  }

  private initialiseStripeCard(paymentGateway: PaymentGatewayModel) {
    this.paymentGateway = paymentGateway;
    this.stripe = Stripe(paymentGateway.publishableKey, { stripeAccount: paymentGateway.accountStripeConnectId || undefined });
    $('.stripe-elements').show(); // TO DO: CONVERT TO KNOCKOUTJS

    const elements = this.stripe.elements();

      // Custom styling can be passed to options when creating an Element.
      const style = {
          base: {
              // Add your base input styles here. For example:
              fontSize: '16px',
              color: "#32325D",
              '::placeholder': {
                  color: '#DEDEDE'
              }
          }
      };

      this.stripeCardNumberElement = elements.create('cardNumber', { style: style, placeholder: 'XXXX XXXX XXXX XXXX' });
      const stripeCardExpiryElement = elements.create('cardExpiry', { style: style });
      const stripeCvvElement = elements.create('cardCvc', { style: style, placeholder: 'XXX' });

      this.stripeCardNumberElement.mount(StripeCardNumberElementId);
      stripeCardExpiryElement.mount('#card-expiry');
      stripeCvvElement.mount('#card-cvv');

      this.stripeCardNumberElement.on('change', (event) => {
          const brandUpperCase = event.brand.toUpperCase();

          this.onCardBrandChange(event.brand, '#card-number', '#card-brand', '#surcharge-info');

          if (brandUpperCase !== 'UNKNOWN' && !paymentGateway.paymentTypes.some(x => x.name.toUpperCase() == brandUpperCase)) {
              document.getElementById('card-number')?.classList.add('is-invalid');
              $('.cardnumber-error').html(this.getCardAvailableCardsMessage(paymentGateway.paymentTypes.map(x => x.name)));
              this.cardPaymentTypeAllowed(false);

              return;
          }

          this.cardPaymentTypeAllowed(true);
          document.getElementById('card-number')?.classList.remove('is-invalid');

          const displayError = $('.cardnumber-error');
          if (event.complete && !event.error) {
              displayError.html('');
          } else {
              if (event.error) {
                  displayError.html(event.error.message);
              } else {
                  displayError.html('');
              }
          }
      });

      stripeCardExpiryElement.on('change', (event) => {
          const $displayError = $('.card-expiry-error'); // TO DO: Convert to Knockout binding
          if (event.complete && !event.error) {
              $displayError.html('');
          } else {
              if (event.error) {
                  $displayError.html(event.error.message);
              } else {
                  $displayError.html('');
              }
          }
      });

      stripeCvvElement.on('change', (event) => {
          var $displayError = $('.card-cvv-error');
          if (event.complete && !event.error) {
              $displayError.html('');
          } else {
              if (event.error) {
                  $displayError.html(event.error.message);
              } else {
                  $displayError.html('');
              }
          }

      });
  }


  private getCardAvailableCardsMessage(cards: string[]) {
    let msg = 'Card type not currently supported, please use ';
    if (cards?.length > 1) {
      msg += cards.slice(0, -1).join(', ') + ' or ' + cards.slice(-1);
    } else {
      msg += cards[0];
    }
    return msg + '.';
  }

  private isValidGateway(paymentGateway: PaymentGatewayModel): boolean {
    if (!paymentGateway.isActive) return false;
    if (!paymentGateway.publishableKey) return false;

    return [PAYMENT_GATEWAY.STRIPE, PAYMENT_GATEWAY.STRIPE_CONNECT].includes(paymentGateway.apiTypeName);
  }
}

